// Presentation follows app verification. The Shopify Function authorises discounts.
export const ACCESS_KEY = 'elk:secret-sale:checkout:v1';
export const CART_ATTRIBUTE = '_elk_secret_sale';
const TOKEN = /^v1\.[A-Za-z0-9_-]+\.[a-f0-9]{64}$/;

function validToken(value) {
  return typeof value === 'string' && value.length <= 4096 && TOKEN.test(value);
}

export function createSecretSaleAccess({fetch: request, storage, root = '/', proxyPath = '/apps/elk-checkout', collectionId, percentage, onChange = () => {}}) {
  // Only same-origin Shopify routes are accepted, including locale prefixes.
  if (!/^\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]*$/.test(root)) throw new Error('Invalid cart root');
  if (!root.endsWith('/')) root += '/';
  if (proxyPath !== '/apps/elk-checkout') throw new Error('Invalid app proxy');
  const proxy = proxyPath;
  const cartUrl = `${root}cart.js`;
  const updateUrl = `${root}cart/update.js`;
  let generation = 0;
  let abort;
  let state = {unlocked: false, checking: true, busy: false, available: false, message: ''};

  function publish(change) {
    state = {...state, ...change};
    onChange({...state});
  }

  function storedToken() {
    try { return storage?.getItem(ACCESS_KEY); } catch { return null; }
  }

  function remember(token) {
    try {
      if (token) storage?.setItem(ACCESS_KEY, token);
      else storage?.removeItem(ACCESS_KEY);
    } catch { /* The confirmed Shopify cart attribute also persists access. */ }
  }

  function matches(campaign) {
    return campaign?.active === true && typeof campaign.campaignId === 'string' &&
      Number.isSafeInteger(campaign.revision) && campaign.revision > 0 &&
      Array.isArray(campaign.collectionIds) && campaign.collectionIds.includes(collectionId) &&
      Number(campaign.percentage) === percentage && percentage > 0 && percentage <= 100;
  }

  function begin({preserveVerifiedDisplay = false, checking = false} = {}) {
    abort?.abort();
    abort = new AbortController();
    const run = {id: ++generation, signal: abort.signal};
    publish({unlocked: preserveVerifiedDisplay && state.unlocked, checking, busy: true, message: ''});
    return run;
  }

  function current(run) {
    if (run.id !== generation || run.signal.aborted) throw new DOMException('Superseded', 'AbortError');
  }

  async function json(url, body, run) {
    current(run);
    const timeout = new AbortController();
    const cancel = () => timeout.abort();
    run.signal.addEventListener('abort', cancel, {once: true});
    const timer = setTimeout(cancel, 10000);
    try {
      const response = await request(url, {
        method: body === undefined ? 'GET' : 'POST',
        credentials: 'same-origin', cache: 'no-store', signal: timeout.signal,
        headers: body === undefined ? {Accept: 'application/json'} : {
          Accept: 'application/json', 'Content-Type': 'application/json',
        },
        ...(body === undefined ? {} : {body: JSON.stringify(body)}),
      });
      current(run);
      if (!response.ok) throw Object.assign(new Error('Request failed'), {status: response.status});
      const value = await response.json();
      current(run);
      return value;
    } finally {
      clearTimeout(timer);
      run.signal.removeEventListener('abort', cancel);
    }
  }

  function requireCampaign(campaign) {
    if (!matches(campaign)) throw Object.assign(new Error('Campaign unavailable'), {campaign: true});
  }

  async function attach(token, cart, run) {
    if (!validToken(token)) throw new Error('Invalid access response');
    if (cart?.attributes?.[CART_ATTRIBUTE] !== token) {
      cart = await json(updateUrl, {attributes: {[CART_ATTRIBUTE]: token}}, run);
    }
    current(run);
    if (cart?.attributes?.[CART_ATTRIBUTE] !== token) throw new Error('Access was not saved to the cart');
    remember(token);
    publish({unlocked: true, available: true, message: ''});
  }

  function failed(error, run, enteringPassword) {
    if (run.id !== generation) return;
    let message = 'Unable to verify sale access. Please try again.';
    const unavailable = error.campaign || error.status === 403;
    if (unavailable) message = 'This sale is unavailable or its settings are being updated. Refresh and try again later.';
    else if (error.status === 429) message = 'Too many attempts. Please wait a moment and try again.';
    else if (enteringPassword && error.status === 401) message = 'Incorrect password. Please try again.';
    publish({unlocked: false, available: !unavailable, message});
  }

  async function restore() {
    const run = begin({preserveVerifiedDisplay: true, checking: true});
    try {
      const campaign = await json(`${proxy}/campaign`, undefined, run);
      requireCampaign(campaign);
      publish({available: true});
      const cart = await json(cartUrl, undefined, run);
      const candidates = [...new Set([cart?.attributes?.[CART_ATTRIBUTE], storedToken()].filter(validToken))];
      for (const token of candidates) {
        try {
          const authorised = await json(`${proxy}/status`, {token}, run);
          requireCampaign(authorised);
          await attach(token, cart, run);
          return true;
        } catch (error) {
          current(run);
          if (error.status !== 401) throw error;
          if (storedToken() === token) remember(null);
        }
      }
      publish({unlocked: false, available: true});
      return false;
    } catch (error) {
      failed(error, run, false);
      return false;
    } finally {
      if (run.id === generation) publish({checking: false, busy: false});
    }
  }

  async function unlock(password) {
    const run = begin();
    try {
      const authorised = await json(`${proxy}/unlock`, {password}, run);
      requireCampaign(authorised);
      await attach(authorised.token, null, run);
      return true;
    } catch (error) {
      failed(error, run, true);
      return false;
    } finally {
      if (run.id === generation) publish({busy: false});
    }
  }

  function lock({checking = false} = {}) {
    generation += 1;
    abort?.abort();
    publish({unlocked: false, checking, busy: false, message: ''});
  }

  return {restore, unlock, lock, getState: () => ({...state})};
}

function mountSecretSale(config) {
  let unlocked = false;
  let verificationTimer;
  const subscribers = new Set();
  const forms = new WeakSet();
  let access;

  class SecretSaleContent extends HTMLElement {
    connectedCallback() {
      if (!this.publicContent) {
        const template = this.querySelector(':scope > template[data-secret-sale-private]');
        if (!template) return;
        this.privateContent = template.content.cloneNode(true);
        template.remove();
        this.publicContent = document.createDocumentFragment();
        this.showingPrivate = false;
      }
      subscribers.add(this);
      this.update();
    }
    disconnectedCallback() { subscribers.delete(this); }
    update() {
      if (this.showingPrivate === unlocked) return;
      const outgoing = unlocked ? this.publicContent : this.privateContent;
      while (this.firstChild) outgoing.append(this.firstChild);
      this.append(unlocked ? this.privateContent : this.publicContent);
      this.showingPrivate = unlocked;
    }
  }

  function update(state = access?.getState()) {
    if (!state) return;
    unlocked = state.unlocked;
    for (const element of subscribers) element.update();
    document.querySelectorAll('[data-secret-sale-collection]').forEach(content => {
      content.hidden = !unlocked;
      content.inert = !unlocked;
    });
    document.querySelectorAll('[data-secret-sale-gate]').forEach(gate => {
      gate.hidden = unlocked || state.checking;
    });
    document.querySelectorAll('[data-secret-sale-loading]').forEach(loading => {
      loading.hidden = !state.checking || unlocked;
      loading.dataset.initialized = 'true';
    });
    document.querySelectorAll('[data-secret-sale-form]').forEach(form => {
      form.querySelector('fieldset').disabled = !state.available || state.busy;
      form.querySelector('button').disabled = state.busy;
      form.querySelector('[role="status"]').textContent = state.message;
    });
    clearTimeout(verificationTimer);
    if (unlocked) verificationTimer = setTimeout(() => access.restore(), 60000);
  }

  let storage;
  try { storage = window.localStorage; } catch { /* A cart can remember access without local storage. */ }
  access = createSecretSaleAccess({
    fetch: window.fetch.bind(window), storage,
    root: config.dataset.cartRoot || '/', proxyPath: config.dataset.proxyPath,
    collectionId: config.dataset.collectionId,
    percentage: Number(config.dataset.percentage), onChange: update,
  });
  if (!customElements.get('secret-sale-content')) customElements.define('secret-sale-content', SecretSaleContent);

  function bindForms() {
    document.querySelectorAll('[data-secret-sale-form]').forEach(form => {
      if (forms.has(form)) return;
      forms.add(form);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (access.getState().busy) return;
        const input = form.elements.password;
        const password = input.value;
        input.value = '';
        const success = await access.unlock(password);
        if (!success) {
          input.setAttribute('aria-invalid', 'true');
          if (!access.getState().busy) input.focus();
          return;
        }
        input.removeAttribute('aria-invalid');
        const content = document.querySelector('[data-secret-sale-collection]');
        if (content) { content.tabIndex = -1; content.focus(); }
      });
    });
    update();
  }

  bindForms();
  access.restore();
  document.addEventListener('shopify:section:load', bindForms);
  // A reload refreshes variant estimates and any cached public/private component markup.
  window.addEventListener('storage', event => {
    if (event.key === ACCESS_KEY || event.key === null) { access.lock({checking: true}); window.location.reload(); }
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) { access.lock({checking: true}); access.restore(); }
  });
  document.addEventListener('visibilitychange', () => {
    const state = access.getState();
    if (document.visibilityState === 'visible' && state.unlocked && !state.busy) access.restore();
  });
}

if (typeof document !== 'undefined') {
  const config = document.querySelector('meta[name="elk-secret-sale"]');
  if (config) mountSecretSale(config);
}
